import { useEffect, useRef } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useYouTubeIframeAPI } from '@/hooks/useYouTubeIframeAPI';
import { useVideoPlayerStore } from '@/store/videoPlayerStore';
import type { VideoPlayerState } from '@/store/videoPlayerStore';

interface YouTubeSegmentModalProps {
  open: boolean;
  onClose: () => void;
  /** The YouTube video ID (e.g. "dQw4w9WgXcQ"). */
  youtubeVideoId: string;
  startTime: number;
  endTime: number;
  videoTitle: string;
}

/**
 * Map YouTube IFrame API PlayerState integers to our normalised VideoPlayerState.
 * Mirrors mapYTState in YouTubePlayer.tsx — these two components are the ONLY writers
 * of videoPlayerStore.playerState (architecture.md line 157).
 */
function mapYTState(ytState: number): VideoPlayerState {
  switch (ytState) {
    case 1:
      return 'playing';
    case 2:
      return 'paused';
    case 3:
      return 'buffering';
    case 0:
      return 'idle';
    default:
      return 'idle';
  }
}

/**
 * YouTubeSegmentModal — minimal YouTube player in a MUI Dialog for vocabulary review.
 *
 * - Opens pre-seeked to the segment's start time; does NOT autoplay (AC 2, UX spec line 126).
 * - Loops the segment automatically when playback reaches endTime (AC 3).
 * - Writes videoPlayerStore.playerState on state changes so useYouTubeListeningTracker
 *   captures listening time transparently — the modal does NOT import the tracker (AC 2).
 * - Sets playerState = 'idle' on close so the tracker stops cleanly (AC 4).
 * - Uses useYouTubeIframeAPI which is idempotent — safe to call even if the API was
 *   already loaded by YouTubePlayer.tsx (AC 6).
 */
export const YouTubeSegmentModal = ({
  open,
  onClose,
  youtubeVideoId,
  startTime,
  endTime,
  videoTitle,
}: YouTubeSegmentModalProps) => {
  const { ready: iframeReady } = useYouTubeIframeAPI();

  // Generate a stable unique DOM ID per modal instance to avoid collisions on remount.
  const playerDivId = useRef(`yt-segment-modal-${Date.now()}`);
  const playerRef = useRef<YTPlayer | null>(null);
  const loopIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Keep latest prop values accessible inside closures without re-creating the player.
  const startTimeRef = useRef(startTime);
  const endTimeRef = useRef(endTime);
  useEffect(() => { startTimeRef.current = startTime; }, [startTime]);
  useEffect(() => { endTimeRef.current = endTime; }, [endTime]);

  /** Stop the loop-polling interval. */
  const stopLoopInterval = () => {
    if (loopIntervalRef.current !== null) {
      clearInterval(loopIntervalRef.current);
      loopIntervalRef.current = null;
    }
  };

  /** Destroy the YT player instance and clean up the polling interval. */
  const destroyPlayer = () => {
    stopLoopInterval();
    if (playerRef.current) {
      try {
        playerRef.current.destroy();
      } catch {
        // Ignore — the DOM node may already be detached.
      }
      playerRef.current = null;
    }
  };

  /** Full close-and-cleanup handler (AC 4). */
  const handleClose = () => {
    destroyPlayer();
    // Set playerState = 'idle' BEFORE calling onClose so the listening tracker stops
    // recording before the component tree changes (architecture.md lines 742–744).
    useVideoPlayerStore.getState().setPlayerState('idle');
    onClose();
  };

  // Initialise the player when both the modal is open and the IFrame API is ready.
  useEffect(() => {
    if (!open || !iframeReady) return;

    // Guard against double-init (React StrictMode or repeated re-renders).
    if (playerRef.current) return;

    const player = new window.YT.Player(playerDivId.current, {
      videoId: youtubeVideoId,
      playerVars: {
        autoplay: 0,         // AC 2, UX spec line 126: no autoplay
        controls: 1,
        rel: 0,
        modestbranding: 1,
        playsinline: 1,
      },
      events: {
        onReady: (event) => {
          // Seek to the segment start so the user can see the right frame before pressing play.
          // Do NOT call playVideo() — the user must initiate playback explicitly (AC 2).
          event.target.seekTo(startTimeRef.current, true);
          // Start segment loop polling (250 ms) here — after onReady — so that
          // getCurrentTime() is guaranteed to exist (AC 3). Starting the interval
          // before onReady fires causes "getCurrentTime is not a function" errors
          // because the YT IFrame API only exposes player methods once it is ready.
          if (!loopIntervalRef.current) {
            loopIntervalRef.current = setInterval(() => {
              if (playerRef.current) {
                const currentTime = playerRef.current.getCurrentTime();
                if (currentTime >= endTimeRef.current) {
                  playerRef.current.seekTo(startTimeRef.current, true);
                }
              }
            }, 250);
          }
        },
        onStateChange: (event) => {
          const ytState = event.data;
          // Ad detection: PLAYING while getDuration() === 0 means an ad is playing.
          let normalized: VideoPlayerState;
          if (ytState === 1 && event.target.getDuration() === 0) {
            normalized = 'ad';
          } else {
            normalized = mapYTState(ytState);
          }
          useVideoPlayerStore.getState().setPlayerState(normalized);
        },
      },
    });

    playerRef.current = player;

    return () => {
      // Cleanup if the effect re-runs (e.g. open/iframeReady flipped).
      destroyPlayer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, iframeReady, youtubeVideoId]);

  // Detect external close (open transitions true → false) and clean up (AC 4, Task 3.10).
  useEffect(() => {
    if (!open && playerRef.current) {
      destroyPlayer();
      useVideoPlayerStore.getState().setPlayerState('idle');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      keepMounted
      sx={{ zIndex: 1400 }}
      aria-label={`Play YouTube segment: ${videoTitle}`}
    >
      <DialogTitle sx={{ pr: 6 }}>
        {videoTitle}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* 16:9 aspect-ratio container — same pattern as YouTubePlayer.tsx */}
        <Box sx={{ position: 'relative', paddingTop: '56.25%', width: '100%', bgcolor: 'black' }}>
          {!iframeReady && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
              }}
            >
              <CircularProgress color="inherit" sx={{ color: 'white' }} />
              <Typography variant="caption" color="white">Loading player…</Typography>
            </Box>
          )}
          <Box
            id={playerDivId.current}
            title={`YouTube video player — ${videoTitle}`}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: '100%',
              height: '100%',
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={handleClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
